import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import { Icon, Mark } from '../ui'

export function LoginScreen() {
  const { signInWithEmail, signInWithProvider, resetPassword } = useAuth()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim()) { toast('Enter your district email'); return }
    if (!password) { toast('Enter your password'); return }
    setLoading(true)
    const { error } = await signInWithEmail(email, password)
    setLoading(false)
    if (error) toast(error)
  }

  async function handleSso(provider: 'google' | 'azure') {
    setLoading(true)
    const { error } = await signInWithProvider(provider)
    // On success the browser redirects; we only land here on failure.
    setLoading(false)
    if (error) toast(`Single sign-on isn't available yet: ${error}`)
  }

  async function handleForgot() {
    if (!email.trim()) { toast('Enter your district email first, then tap "Forgot password"'); return }
    const { error } = await resetPassword(email.trim())
    toast(error ? `Could not send the reset email: ${error}` : 'Password reset sent — check your district email')
  }

  return (
    <div className="login-wrap">
      {/* Brand panel */}
      <div className="login-brand">
        <div className="lb-mark">
          <div className="mark"><Mark /></div>
          <div>
            <div>GrowthLane</div>
            <span className="sub">Professional Learning</span>
          </div>
        </div>

        <div className="lb-body">
          <h2>A better way to grow as an educator.</h2>
          <p className="lb-sub">
            The district's connected platform for professional learning plans —
            built around the Danielson Framework and your evaluation cycle.
          </p>

          <div className="login-cadence">
            {[
              { dot: '#b3823a', title: 'Fall Self-Reflection', date: 'Due Oct 15' },
              { dot: '#2f6f82', title: 'Midyear Check-In',    date: 'Due Feb 6' },
              { dot: '#4a8a60', title: 'Spring Summative',    date: 'Due Mar 6' },
            ].map((step, i) => (
              <div key={i} className="lc-step">
                <div className="lc-dot" style={{ background: step.dot }} />
                {i < 2 && <div className="lc-bar" />}
                <div className="lc-t">{step.title}</div>
                <div className="lc-d">{step.date}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lb-foot">
          SSO · FERPA compliant · Role-based access · Supabase-backed
        </div>
      </div>

      {/* Sign-in card */}
      <div className="login-main">
        <div className="login-card">
          {/* Mobile logo */}
          <div className="lm-mark">
            <div className="mark"><Mark /></div>
            <span>GrowthLane</span>
          </div>

          <h1>Sign in</h1>
          <p className="lm-sub">Use your district email and password</p>

          {/* District SSO — providers are enabled in the Supabase dashboard */}
          <button className="sso-btn" type="button" onClick={() => handleSso('google')} disabled={loading}>
            <GoogleG />
            Sign in with Google
          </button>

          <button className="sso-btn" type="button" onClick={() => handleSso('azure')} disabled={loading}>
            <MicrosoftLogo />
            Sign in with Microsoft
          </button>

          <div className="login-or">or</div>

          <form onSubmit={handleEmailSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="email">District email</label>
              <input
                id="email"
                className="inp"
                type="email"
                autoComplete="email"
                placeholder="you@cvsd.org"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="login-field">
              <div className="fl-row">
                <label htmlFor="password">Password</label>
                <button type="button" className="link-btn" onClick={handleForgot}>Forgot password?</button>
              </div>
              <input
                id="password"
                className="inp"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-pri btn-lg"
              style={{ width: '100%', marginTop: 8 }}
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign in'}
              {!loading && <Icon name="arrowR" size={18} />}
            </button>
          </form>

          <div className="login-foot">
            Need access? Contact your district administrator
            <div className="powered">
              Powered by GrowthLane
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  )
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21">
      <rect x="1"  y="1"  width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1"  width="9" height="9" fill="#7fba00"/>
      <rect x="1"  y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}
